import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { dataService } from '../../../services/dataService';
import { Student, LearningPath, LearningPathStandard, Achievement, PathAssignment, StandardProgress } from '../../../data/types';
import { GameIcons } from '../../styles/GameIcons';
import '../../styles/GameStyleUI.css';
import './LearningPathCarousel.css';
import './StudentDashboardV3.css'; // Import v3-specific styles
import GameNavButtons from '../../common/GameNavButtons';
import LearningPathCarousel from '../../common/LearningPathCarousel';
import { motion } from 'framer-motion';

// Extend Student type to include additional properties used in this component
interface ExtendedStudent extends Student {
  completedTasks?: number;
  streak?: number;
}

const StudentDashboardV3: React.FC = () => {
  const navigate = useNavigate();
  // State declarations
  const [student, setStudent] = useState<ExtendedStudent | null>(null);
  const [activePaths, setActivePaths] = useState<LearningPath[]>([]);
  const [currentStandard, setCurrentStandard] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [leaderboard, setLeaderboard] = useState<{name: string, rank: number, points: number}[]>([]);
  const [engagementMessage, setEngagementMessage] = useState<{message: string}>({message: 'Complete your daily math challenge to earn bonus points!'});
  const [standardDetailsMap, setStandardDetailsMap] = useState<Record<string, any>>({});
  const [level, setLevel] = useState<{name: string, current: number, next: number, progress: number}>({
    name: 'Math Novice',
    current: 1,
    next: 2,
    progress: 0
  });

  // Level definitions based on points - wrapped in useMemo to prevent recreation on every render
  const levelDefinitions = useMemo(() => [
    { threshold: 0, name: "Math Novice" },
    { threshold: 100, name: "Number Explorer" },
    { threshold: 250, name: "Calculation Cadet" },
    { threshold: 500, name: "Problem Solver" },
    { threshold: 1000, name: "Math Adventurer" },
    { threshold: 2000, name: "Formula Master" },
    { threshold: 3500, name: "Math Wizard" },
    { threshold: 5000, name: "Math Champion" },
    { threshold: 7500, name: "Math Superhero" },
    { threshold: 10000, name: "Math Legend" }
  ], []);

  useEffect(() => {
    // Initialize dashboard data
    
    // Fetch student data
    const fetchStudentData = async () => {
      try {
        const studentId = localStorage.getItem('activeStudentId');
        if (!studentId) {
          console.error("Student ID not found in localStorage");
          setLoading(false);
          return;
        }

        // Fetch student data from service
        const allStudents = await dataService.getStudents();
        const currentStudent = allStudents.find(s => s.id === studentId);
        
        if (currentStudent) {
          setStudent(currentStudent);
          
          // Calculate student level based on points
          const totalPoints = currentStudent.totalPoints || 0;
          let currentLevel = levelDefinitions[0];
          let nextLevel = levelDefinitions[1];
          
          for (let i = levelDefinitions.length - 1; i >= 0; i--) {
            if (totalPoints >= levelDefinitions[i].threshold) {
              currentLevel = levelDefinitions[i];
              nextLevel = levelDefinitions[i + 1] || levelDefinitions[i];
              break;
            }
          }
          
          const progress = nextLevel === currentLevel ? 
            100 : 
            ((totalPoints - currentLevel.threshold) / (nextLevel.threshold - currentLevel.threshold)) * 100;
          
          setLevel({
            name: currentLevel.name,
            current: levelDefinitions.indexOf(currentLevel) + 1,
            next: levelDefinitions.indexOf(nextLevel) + 1,
            progress: progress
          });
          
          // Set achievements
          setAchievements(currentStudent.achievements || []);
          
          // Fetch active learning paths using dataService instead of direct import
          const studentPaths = await dataService.getStudentLearningPaths(studentId);
          setActivePaths(studentPaths);
          
          // Get standard details map from dataService and set it in state
          const standardDetails = await dataService.getStandardDetailsMap();
          setStandardDetailsMap(standardDetails);
          
          // Find current standard the student is working on
          if (studentPaths.length > 0) {
            const priorityPath = studentPaths.find(path => 
              path.assignedTo.some(assignment => 
                assignment.assignedToId === studentId && assignment.isPriority
              )
            ) || studentPaths[0];
            
            if (priorityPath.standards && priorityPath.standards.length > 0) {
              // Sort standards by sequence order
              const sortedStandards = [...priorityPath.standards].sort(
                (a, b) => a.sequenceOrder - b.sequenceOrder
              );
              
              // Find the first incomplete standard
              for (const standard of sortedStandards) {
                const progress = currentStudent.progress[standard.standardId];
                if (!progress || progress.learningPhaseStatus !== 'Completed') {
                  setCurrentStandard(standard.standardId);
                  break;
                }
              }
            }
          }
          
          // Generate mock leaderboard data
          setLeaderboard([
            { name: "MathWhiz", rank: 1, points: totalPoints + 120 },
            { name: "NumberNinja", rank: 2, points: totalPoints + 85 },
            { name: "AlgebraAce", rank: 3, points: totalPoints + 50 },
            { name: "You", rank: 4, points: totalPoints },
            { name: "MathExplorer", rank: 5, points: totalPoints - 30 }
          ]);
          
          // Generate a random engagement message
          const messages = [
            { message: 'Complete your daily math challenge to earn bonus points!' },
            { message: 'You\'re on a 5-day streak! Keep it up!' },
            { message: 'New achievements are waiting to be unlocked!' },
            { message: 'Your teacher just assigned a new learning path!' },
            { message: 'You\'re making great progress on fractions!' },
          ];
          
          setEngagementMessage(messages[Math.floor(Math.random() * messages.length)]);
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching student data:", error);
        setLoading(false);
      }
    };
    
    fetchStudentData();
  }, [levelDefinitions]); // Updated dependencies after removing welcomeMessages
  
  // Helper function to determine which learning step icons should be displayed
  const getLearningStepIcons = (progress: StandardProgress | undefined) => {
    // Default values if progress is undefined
    const showVideo = true; // Always show video icon as it's the first step
    const showQuiz = progress?.completedSteps?.video || 
                     progress?.learningPhaseStatus === 'VideoCompleted' || 
                     progress?.learningPhaseStatus === 'PracticeCompleted' || 
                     progress?.learningPhaseStatus === 'Completed';
    const showGame = progress?.completedSteps?.quiz || 
                    progress?.learningPhaseStatus === 'PracticeCompleted' || 
                    progress?.learningPhaseStatus === 'Completed';
                    
    return { showVideo, showQuiz, showGame };
  };

  // Helper function to determine the learning phase icon
  const getLearningPhaseIcon = (status: string | undefined) => {
    if (!status || status === 'NotStarted') {
      return (
        <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
          </svg>
        </div>
      );
    } else if (status === 'VideoCompleted') {
      return (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center shadow-md">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
            <path d="M14 6a2 2 0 012-2h2a2 2 0 012 2v8a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z" />
          </svg>
        </div>
      );
    } else if (status === 'PracticeCompleted') {
      return (
        <div className="w-8 h-8 rounded-full bg-yellow-500 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
          </svg>
        </div>
      );
    } else if (status === 'Completed') {
      return (
        <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        </div>
      );
    }
    return null;
  };

  // Helper function to render stars based on completion level
  const renderStars = (standardId: string, max: number = 5) => {
    if (!student || !student.progress[standardId]) return null;
    
    const progress = student.progress[standardId];
    let filledStars = 0;
    
    if (progress.learningPhaseStatus === 'Completed') {
      // Calculate stars based on accuracy
      filledStars = progress.practiceAccuracy ? Math.ceil((progress.practiceAccuracy / 100) * max) : max;
    }
    
    return (
      <div className="flex space-x-1">
        {Array.from({ length: max }).map((_, index) => (
          <svg 
            key={index}
            xmlns="http://www.w3.org/2000/svg" 
            className={`h-5 w-5 ${index < filledStars ? 'text-yellow-300' : 'text-gray-400'}`} 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };
  
  const renderStandard = (pathId: string, standard: LearningPathStandard, index: number, isPathLocked: boolean = false) => {
    if (!student) return null;
    const standardDetail = standardDetailsMap[standard.standardId];
    const description = standardDetail?.description || '';
    // Use code property instead of name which doesn't exist on Standard type
    const standardCode = standardDetail?.code || `Standard ${index + 1}`;
    
    // Check if the standard is completed anywhere (not just in this path)
    const globalProgress = student?.progress[standard.standardId];
    const isCompleted = globalProgress?.learningPhaseStatus === 'Completed';
    const isActive = standard.standardId === currentStandard;
    // If the path is locked and this standard is not completed, show locked style
    const isLocked = isPathLocked && !isCompleted;
    
    // Determine if we should show a standard as available even in a locked path
    // If a standard is completed globally, it should be shown as completed regardless of path lock status
    const showAsCompleted = isCompleted;
    const showAsLocked = isPathLocked && !isCompleted;
    
    return (
      <div
        key={`${pathId}-${standard.standardId}`}
        className={`standard-item ${showAsLocked ? 'standard-item-locked' : ''} relative p-4 rounded-lg mb-2 flex items-start ${isActive ? 'bg-gradient-to-r from-blue-500 to-blue-600' : showAsCompleted ? 'bg-gradient-to-r from-green-50 to-green-100 border border-green-300' : showAsLocked ? 'bg-gradient-to-r from-gray-100 to-gray-200 border border-gray-300 opacity-80' : 'bg-gradient-to-r from-gray-100 to-gray-200 border border-gray-300'}`}
      >
        <div className="flex-shrink-0 mr-4">
          {/* Always show sequence number, even for locked standards */}
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-indigo-500 flex items-center justify-center text-white font-bold shadow-md">
            {index + 1}
          </div>
        </div>
        <div className="flex-grow">
          <div className="flex justify-between items-start">
            <span 
              className={`standard-description text-base font-normal block ${isActive ? 'text-white' : 'text-gray-800'} overflow-hidden text-ellipsis max-w-[250px]`}
              title={description}
              style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', whiteSpace: 'normal', fontSize: '1rem' }}
            >
              {description}
              {description.length > 0 && (
                <span className="ml-0.5 text-xs text-blue-400 cursor-pointer align-top" title={description}>ⓘ</span>
              )}
            </span>
            <div className="ml-2 flex-shrink-0 flex flex-col items-end">
              {/* Only show stars for completed standards or active standards, not for locked ones */}
              {(showAsCompleted || !showAsLocked) && (
                <div className="mb-0.5">
                  {renderStars(standard.standardId)}
                </div>
              )}
              
              {/* Status icon in the red circle position */}
              {showAsCompleted ? (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-md">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              ) : showAsLocked ? (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center shadow-md">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-100" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                </div>
              ) : (
                <div className="w-8 h-8 opacity-0">  {/* Placeholder for alignment */}
                </div>
              )}
            </div>
          </div>
          
          {/* Learning phase icons - only shown for active standards */}
          {isActive && (
            <div className="mt-1 flex justify-between items-center">
              <div className="flex space-x-1">
                {/* Show the learning phase status icon */}
                {getLearningPhaseIcon(globalProgress?.learningPhaseStatus)}
                
                {/* Get which learning step icons to display */}
                {(() => {
                  const { showQuiz, showGame } = getLearningStepIcons(globalProgress);
                  
                  return (
                    <>
                      {/* Show the quiz icon only if video is completed or if student is currently on the quiz step */}
                      {showQuiz && (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center shadow-md">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                      
                      {/* Show the game icon only if quiz is completed or if student is currently on the game step */}
                      {showGame && (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-md">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.5 3a1.5 1.5 0 011.5 1.5v3.6a1.5 1.5 0 01-.75 1.3L11 12v3a1 1 0 01-1 1H6a1 1 0 01-1-1v-3L1.75 9.4a1.5 1.5 0 01-.75-1.3V4.5A1.5 1.5 0 012.5 3h11z" />
                          </svg>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              
              {/* Continue button */}
              <button 
                onClick={() => {
                  console.log('Navigating to learning phase with:', { pathId, standardId: standard.standardId });
                  // Make sure we're passing valid IDs
                  if (pathId && standard.standardId) {
                    try {
                      // First, store the current standard and path info in localStorage
                      localStorage.setItem('currentPathId', pathId);
                      localStorage.setItem('currentStandardId', standard.standardId);
                      
                      // Pre-fetch data if possible to make transition smoother
                      const prefetchData = async () => {
                        try {
                          // This will warm up the data cache
                          await Promise.all([
                            dataService.getLearningPaths(),
                            dataService.getStandards(),
                            dataService.getQuestions(),
                            dataService.getStudents()
                          ]);
                          console.log('Pre-fetched data for learning phase');
                        } catch (error) {
                          console.warn('Pre-fetch failed, but navigation will continue', error);
                        }
                      };
                      
                      // Start pre-fetching but don't wait for it
                      prefetchData();
                      
                      // Navigate to the learning phase screen with state
                      navigate(`/student/learning-v2/${pathId}/${standard.standardId}`, {
                        state: { 
                          fromDashboard: true,
                          timestamp: Date.now() 
                        }
                      });
                    } catch (error) {
                      console.error('Error during navigation preparation:', error);
                      // Fallback to basic navigation if anything fails
                      navigate(`/student/learning-v2/${pathId}/${standard.standardId}`);
                    }
                  } else {
                    console.error('Missing required navigation parameters', { pathId, standardId: standard.standardId });
                  }
                }}
                className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-lg transition-colors shadow-md transform hover:translate-y-px">
                Continue
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

// Helper function to format dates and check deadline status
const formatDate = (dateString?: string) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    month: 'short' as const, 
    day: 'numeric' as const, 
    year: 'numeric' as const 
  });
};

// Helper function to check if a date is today
const isToday = (dateString?: string) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  const today = new Date();
  return date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
};

// Helper function to check if a date is in the past
const isPast = (dateString?: string) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  return date < new Date();
};

// Helper function to generate consistent avatar emojis based on name
const getAvatarEmoji = (name: string) => {
  // Array of possible avatar emojis
  const avatarEmojis = ['👦', '👧', '👨', '👩', '🧑', '👱', '👴', '👵', '🧒'];
  
  // For 'You', always use the same emoji as the student avatar
  if (name === 'You') {
    return '👦';
  }
  
  // Use the name to generate a consistent index
  let nameSum = 0;
  if (name) {
    for (let i = 0; i < name.length; i++) {
      nameSum += name.charCodeAt(i);
    }
  }
  
  // Return a consistent emoji based on the name
  return avatarEmojis[nameSum % avatarEmojis.length];
};

const renderLearningPath = (path: LearningPath, index: number) => {
  if (!student) return null;
  
  // Check if path is locked (needs previous path to be completed)
  const isLocked = index > 0 && activePaths[index - 1]?.standards.some(standard => {
    const progress = student.progress[standard.standardId];
    return !progress || progress.learningPhaseStatus !== 'Completed';
  });
  
  // Calculate path completion percentage
  const totalStandards = path.standards.length;
  const completedStandards = path.standards.filter(standard => {
    const progress = student?.progress[standard.standardId];
    return progress && progress.learningPhaseStatus === 'Completed';
  }).length;
  
  const completionPercentage = totalStandards > 0 ? Math.round((completedStandards / totalStandards) * 100) : 0;
  
  // Get path dates for display - use assignedTo array if available
  // Find assignment for current student to get start and end dates
  const studentAssignment = path.assignedTo?.find(
    assignment => assignment.assignedToId === student.id
  );
  
  const startDate = studentAssignment?.startDate || path.createdAt;
  const endDate = studentAssignment?.endDate;
  
  // Check deadline status - using the current date for comparison
  // For student-001, we've set up paths with today's deadline and missed deadline
  const isDeadlineToday = path.id === 'student-path-002'; // Advanced Problem Solving
  const isDeadlinePassed = path.id === 'student-path-003'; // Geometry Exploration
  const showDeadlineAlert = isDeadlineToday || isDeadlinePassed;
  
  return (
    <div 
      key={path.id}
      className={`learning-path-block mb-6 p-6 rounded-xl shadow-lg ${isLocked ? 'learning-path-locked bg-gradient-to-r from-gray-100 to-gray-200 border border-gray-300' : 'bg-gradient-to-r from-white to-blue-50 border border-blue-200'}`}
    >
      <div className={`learning-path-header flex justify-between items-center mb-4 p-4 rounded-lg text-white shadow-md relative ${isDeadlinePassed ? 'bg-gradient-to-r from-red-600 to-red-800 border-2 border-red-300' : isDeadlineToday ? 'bg-gradient-to-r from-orange-500 to-orange-700 border-2 border-yellow-300' : 'bg-gradient-to-r from-teal-600 to-teal-800'}`}>
        {/* Game character reminder */}
        {showDeadlineAlert && (
          <div className="absolute -top-5 -right-2 z-10">
            <div className="relative">
              <GameIcons.Fox size={40} />
              <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                !
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-col">
          <div className="flex items-center">
            {/* Always show sequence number for both locked and unlocked paths */}
            <div className="w-8 h-8 rounded-full bg-white text-indigo-600 flex items-center justify-center font-bold mr-2 shadow-sm">
              {index + 1}
            </div>
            <h3 className="text-lg font-bold">{path.name}</h3>
            
            {/* Show priority icon if path is priority */}
            {studentAssignment?.isPriority && (
              <div 
                className="ml-2 flex items-center justify-center"
                title="Priority Learning Path"
              >
                <GameIcons.Lightning 
                  size={24} 
                  color="#FFEB3B" 
                  className="animate-pulse"
                />
              </div>
            )}
            
            {/* Show deadline alert if needed */}
            {showDeadlineAlert && (
              <div 
                className={`ml-2 flex items-center justify-center ${isDeadlinePassed ? 'animate-pulse' : ''}`}
                title={isDeadlinePassed ? 'Deadline missed!' : 'Deadline is today!'}
              >
                <GameIcons.Calendar 
                  size={24} 
                  color={isDeadlinePassed ? '#F44336' : '#FFEB3B'} 
                  className={isDeadlinePassed ? 'animate-bounce' : ''}
                />
              </div>
            )}
          </div>
          
          {/* Display start and end dates */}
          {(startDate || endDate) && (
            <div className="text-xs mt-1 font-medium bg-black bg-opacity-20 px-2 py-1 rounded-full inline-block">
              {startDate && <span>Start: {formatDate(startDate)}</span>}
              {startDate && endDate && <span> - </span>}
              {endDate && (
                <span className={`${isDeadlinePassed ? 'text-red-300 font-bold' : isDeadlineToday ? 'text-yellow-200 font-bold' : ''}`}>
                  End: {formatDate(endDate)}
                  {isDeadlinePassed && ' (Missed)'}
                  {isDeadlineToday && ' (Today)'}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center">
          {/* Show either lock icon or completion status */}
          {isLocked ? (
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center shadow-md">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
            </div>
          ) : (
            <div className="text-sm text-white font-medium bg-black bg-opacity-20 px-3 py-1 rounded-full">
              {`${completedStandards}/${totalStandards} completed`}
            </div>
          )}
        </div>
      </div>
      
      <div className="w-full bg-gray-200 rounded-full h-3 mb-5 overflow-hidden shadow-inner">
        <div 
          className={`h-3 rounded-full ${isDeadlinePassed && completionPercentage < 100 ? 'bg-gradient-to-r from-red-300 to-red-500' : 'bg-gradient-to-r from-amber-300 to-yellow-500'}`}
          style={{ width: `${completionPercentage}%` }}
        ></div>
      </div>
      {/* Always render standards, but pass isPathLocked to renderStandard */}
      <div className="space-y-3">
        {path.standards
          .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
          .map((standard, idx) => renderStandard(path.id, standard, idx, isLocked))}
      </div>
    </div>
  );
};

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-bounce text-4xl text-purple-300 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
          Loading...
        </div>
      </div>
    );
  }

  // Helper function to determine the number of columns based on screen width
  const getGridColumns = () => {
    // This will be handled by Tailwind responsive classes
    return "grid-cols-1 md:grid-cols-2 lg:grid-cols-2";
  };

  return (
    <div className="v3-dashboard container mx-auto px-4 pt-0 pb-4 space-y-4" style={{fontFamily: "'Fredoka', sans-serif"}}>
      {/* 1. Welcome Banner - Compact Version */}
      <div className="game-welcome-banner flex flex-col md:flex-row items-start py-1">
        <div className="flex items-start w-full md:w-auto">
          {/* Bigger avatar */}
          <div className="game-avatar-container mr-3">
            <div className="game-avatar-border pulse-animation" style={{ width: 64, height: 64 }}>
              <span className="w-[58px] h-[58px] flex items-center justify-center text-4xl bg-blue-100 rounded-full select-none" role="img" aria-label="Student Avatar">👦</span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col">
              {/* Welcome text aligned with top of avatar */}
              <h1 className="text-lg font-bold text-indigo-900 mb-1">
                Welcome back, {student?.firstName || 'Student'}! <span className="bg-indigo-600 text-white text-xs py-0.5 px-1.5 rounded-full ml-1">+50 XP</span>
              </h1>
              {/* Inline stats for mobile view */}
              <div className="flex items-center space-x-2 text-xs">
                <div className="flex items-center bg-orange-500 text-white px-2 py-0.5 rounded-full">
                  <span className="mr-0.5">🔥</span>
                  <span>{student?.streak || 3}</span>
                  <span className="ml-0.5">day streak</span>
                </div>
                <div className="flex items-center bg-purple-500 text-white px-2 py-0.5 rounded-full">
                  <span className="mr-0.5">⭐</span>
                  <span>Level {level.current}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Engagement Message Block - Central */}
        <div className="w-full md:flex-1 my-3 md:my-0 md:mx-4 flex items-center justify-center">
          <motion.div 
            className="flex items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Animated Character */}
            <motion.div 
              className="mr-3 flex-shrink-0"
              animate={{ 
                y: [0, -8, 0],
                rotate: [0, 3, 0, -3, 0]
              }}
              transition={{ 
                repeat: Infinity, 
                duration: 3,
                ease: "easeInOut" 
              }}
            >
              <div className="relative">
                {/* Character Image with Animated Elements */}
                <div className="relative w-20 h-20 drop-shadow-lg">
                  {/* Base Character Image */}
                  <img 
                    src="/assets/images/characters/G2.png" 
                    alt="Character" 
                    className="w-full h-full object-contain"
                  />
                  
                  {/* Animated Eyes Layer */}
                  <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                    <div className="relative w-full h-full">
                      {/* Left Eye */}
                      <div className="absolute" style={{ left: '30%', top: '35%', width: '12%', height: '12%' }}>
                        <motion.div 
                          className="w-full h-full bg-white rounded-full flex items-center justify-center"
                          animate={{
                            scaleY: [1, 0.1, 1],
                          }}
                          transition={{
                            repeat: Infinity,
                            repeatDelay: 3.5,
                            duration: 0.15,
                          }}
                        >
                          <div className="w-2/3 h-2/3 bg-black rounded-full"></div>
                        </motion.div>
                      </div>
                      
                      {/* Right Eye */}
                      <div className="absolute" style={{ left: '58%', top: '35%', width: '12%', height: '12%' }}>
                        <motion.div 
                          className="w-full h-full bg-white rounded-full flex items-center justify-center"
                          animate={{
                            scaleY: [1, 0.1, 1],
                          }}
                          transition={{
                            repeat: Infinity,
                            repeatDelay: 3.5,
                            duration: 0.15,
                          }}
                        >
                          <div className="w-2/3 h-2/3 bg-black rounded-full"></div>
                        </motion.div>
                      </div>
                      
                      {/* Occasional Eye Shine */}
                      <motion.div
                        className="absolute" 
                        style={{ left: '32%', top: '34%', width: '5%', height: '5%' }}
                        animate={{
                          opacity: [0, 0.8, 0]
                        }}
                        transition={{
                          repeat: Infinity,
                          repeatDelay: 4,
                          duration: 0.5,
                          ease: "easeInOut"
                        }}
                      >
                        <div className="w-full h-full bg-white rounded-full"></div>
                      </motion.div>
                      
                      <motion.div
                        className="absolute" 
                        style={{ left: '60%', top: '34%', width: '5%', height: '5%' }}
                        animate={{
                          opacity: [0, 0.8, 0]
                        }}
                        transition={{
                          repeat: Infinity,
                          repeatDelay: 4,
                          duration: 0.5,
                          ease: "easeInOut"
                        }}
                      >
                        <div className="w-full h-full bg-white rounded-full"></div>
                      </motion.div>
                    </div>
                  </div>
                </div>
                
                {/* Speech Bubble Pointer */}
                <div className="absolute top-10 right-0 w-4 h-4 bg-orange-400 transform rotate-45" 
                  style={{ background: 'linear-gradient(135deg, rgba(255,167,38,0.85) 0%, rgba(255,112,67,0.75) 100%)' }} />
              </div>
            </motion.div>
            
            {/* Message Bubble with Transparency Effect */}
            <motion.div 
              className="relative py-2 px-4 rounded-xl max-w-xs overflow-hidden"
              animate={{ 
                scale: [1, 1.03, 1],
              }}
              transition={{ 
                repeat: Infinity, 
                duration: 2,
                ease: "easeInOut" 
              }}
              style={{
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 167, 38, 0.6)'
              }}
            >
              {/* Gradient background with transparency */}
              <div className="absolute inset-0 z-0 pointer-events-none" 
                style={{background: 'linear-gradient(135deg, rgba(255,167,38,0.85) 0%, rgba(255,112,67,0.75) 50%, rgba(255,138,80,0.65) 100%)'}} />
              
              {/* Animated particles in the background */}
              <motion.div 
                className="absolute inset-0 z-0 overflow-hidden"
                initial={{ opacity: 0.5 }}
                animate={{ opacity: [0.5, 0.7, 0.5] }}
                transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
              >
                {[...Array(5)].map((_, i) => (
                  <motion.div 
                    key={i}
                    className="absolute rounded-full bg-white"
                    style={{
                      width: Math.random() * 4 + 2 + 'px',
                      height: Math.random() * 4 + 2 + 'px',
                      left: Math.random() * 100 + '%',
                      top: Math.random() * 100 + '%',
                      opacity: 0.4
                    }}
                    animate={{
                      y: [0, -10, 0],
                      opacity: [0.4, 0.7, 0.4]
                    }}
                    transition={{
                      repeat: Infinity,
                      duration: 2 + Math.random() * 2,
                      delay: Math.random() * 2
                    }}
                  />
                ))}
              </motion.div>
              
              <span className="font-bold text-base md:text-lg text-white relative z-10 drop-shadow-sm">
                {engagementMessage.message}
              </span>
            </motion.div>
          </motion.div>
        </div>
        
        {/* Compact Stats block on the right, horizontal row format with reduced height */}
        <div className="hidden md:flex flex-row items-center justify-end space-x-3 flex-shrink-0">
          {/* Points Stat - Compact */}
          <div className="game-stat-compact relative flex flex-row items-center px-3 py-2 rounded-xl overflow-hidden border-2 border-yellow-300 shadow-md">
            <div className="absolute inset-0 z-0 pointer-events-none rounded-xl" style={{background: 'linear-gradient(135deg, rgba(255,237,160,0.80) 0%, rgba(255,193,7,0.65) 60%, rgba(255,255,255,0.45) 100%)'}} />
            <GameIcons.Star size={24} color="#FFD700" className="mr-2 relative z-10" />
            <div className="flex flex-col relative z-10">
              <span className="font-bold text-lg text-yellow-900 drop-shadow-sm">{(student?.totalPoints || 0).toLocaleString()}</span>
              <span className="text-xs uppercase font-semibold text-yellow-800 drop-shadow-sm">Points</span>
            </div>
          </div>
          {/* Tasks Stat - Compact */}
          <div className="game-stat-compact relative flex flex-row items-center px-3 py-2 rounded-xl overflow-hidden border-2 border-green-300 shadow-md">
            <div className="absolute inset-0 z-0 pointer-events-none rounded-xl" style={{background: 'linear-gradient(135deg, rgba(200,255,200,0.80) 0%, rgba(76,175,80,0.55) 60%, rgba(255,255,255,0.35) 100%)'}} />
            <GameIcons.Medal size={24} color="#43A047" className="mr-2 relative z-10" />
            <div className="flex flex-col relative z-10">
              <span className="font-bold text-lg text-green-900 drop-shadow-sm">{(student?.completedTasks || 0).toLocaleString()}</span>
              <span className="text-xs uppercase font-semibold text-green-800 drop-shadow-sm">Tasks</span>
            </div>
          </div>
          {/* Streak Stat - Compact */}
          <div className="game-stat-compact relative flex flex-row items-center px-3 py-2 rounded-xl overflow-hidden border-2 border-orange-200 shadow-md">
            <div className="absolute inset-0 z-0 pointer-events-none rounded-xl" style={{background: 'linear-gradient(135deg, rgba(255,225,160,0.80) 0%, rgba(255,193,7,0.55) 60%, rgba(255,255,255,0.35) 100%)'}} />
            <GameIcons.Lightning size={24} color="#FFC107" className="mr-2 relative z-10" />
            <div className="flex flex-col relative z-10">
              <span className="font-bold text-lg text-orange-800 drop-shadow-sm">{(student?.streak || 0).toLocaleString()}</span>
              <span className="text-xs uppercase font-semibold text-orange-700 drop-shadow-sm">Streak</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* 2. Learning Paths and Leaderboard */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Learning Paths Carousel */}
        <div className={`${activePaths.length > 0 ? 'lg:col-span-3' : 'lg:col-span-4'}`}>
          {activePaths.length === 0 ? (
            <div className="bg-white p-6 rounded-xl shadow text-center text-gray-500">
              No learning paths assigned yet. Ask your teacher to assign you a path!
            </div>
          ) : (
            <div className="bg-white p-6 pt-6 pb-4 rounded-xl shadow">
              <div className="mb-6">
                <h2 className="game-heading mb-4 relative">
                  <GameIcons.Book size={28} className="mr-2" color="#29B6F6" />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-blue-400 font-bold text-2xl">Your Learning Adventure</span>
                  <div className="absolute -bottom-1 left-0 w-full h-1 bg-indigo-500 rounded-full"></div>
                </h2>
              </div>
              <LearningPathCarousel 
                paths={activePaths} 
                student={student} 
                renderLearningPath={renderLearningPath}
              />
            </div>
          )}
        </div>

        {/* Leaderboard moved from below */}
        {activePaths.length > 0 && (
          <div className="lg:col-span-1">
            <section className="relative h-full overflow-hidden rounded-xl border-2 border-green-300 shadow-lg">
              {/* Green gradient background with transparency */}
              <div className="absolute inset-0 z-0 pointer-events-none" 
                style={{background: 'linear-gradient(135deg, rgba(200,255,220,0.85) 0%, rgba(72,187,120,0.65) 60%, rgba(220,252,231,0.55) 100%)'}} />
              
              {/* Content with relative positioning to appear above the background */}
              <div className="relative z-10 p-4">
                <h2 className="game-heading mb-4 relative">
                  <GameIcons.Crown size={28} className="mr-2" color="#FFD700" />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-700 to-teal-600 font-bold text-2xl">Class Champions</span>
                  <div className="absolute -bottom-1 left-0 w-full h-1 bg-teal-500 rounded-full"></div>
                </h2>
              
                <div className="space-y-2">
                  {leaderboard.map((entry, index) => (
                    <div 
                      key={index}
                      className={`game-leaderboard-item relative z-10 flex items-center justify-between p-2 rounded-lg ${entry.name === 'You' ? 'bg-teal-100 border border-teal-300' : 'bg-white bg-opacity-60'}`}
                    >
                      <div className="flex items-center">
                        <div className={`game-rank-badge rank-${index < 3 ? index + 1 : 'other'} flex items-center justify-center w-7 h-7 rounded-full ${index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-700' : 'bg-teal-600'} mr-2`}>
                          {index < 3 ? (
                            <GameIcons.Star size={18} color="#FFFFFF" />
                          ) : (
                            <span className="text-white font-bold">{entry.rank}</span>
                          )}
                        </div>
                        {/* Student avatar - bigger size */}
                        <div className="w-10 h-10 mr-2 rounded-full overflow-hidden border-2 border-white flex items-center justify-center bg-blue-100">
                          <span className="text-xl select-none">
                            {getAvatarEmoji(entry.name)}
                          </span>
                        </div>
                        <span className="game-player-name font-medium text-teal-900">{entry.name}</span>
                      </div>
                      <div className="game-points font-bold text-teal-800">{entry.points} pts</div>
                    </div>
                  ))}
                </div>
                
                <button className="game-button bg-teal-600 hover:bg-teal-700 text-white font-bold py-2 px-4 rounded-lg w-full mt-4 flex items-center justify-center">
                  <GameIcons.Crown size={20} className="mr-2" color="#FFFFFF" />
                  View Full Leaderboard
                </button>
              </div>
            </section>
          </div>
        )}
      </div>
      

    </div>
  );
};

export default StudentDashboardV3;
